import { modalActions } from '@/actions';
import { Button } from '@/components/elements/forms/buttons';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import { ListItem } from '@/components/elements/lists';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Icon from '@/components/icons/Icon';
import { EVENT_NAME, SCREEN_NAME } from '@/constants/analytics';
import { trackMpEvent } from '@/helpers';
import { useAppDispatch } from '@/hooks';
import { LoginContextType } from '@/hooks/useLogin';
import { _s } from '@/locale';
import { LoginTriggerSource } from '@/types/analytics';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const translationKey = `RequestToken`;

type RequestTokenProps = Omit<LoginContextType, 'handleLogin' | 'updateView' | 'view' | 'user'> & {
  source: LoginTriggerSource;
  redirectUrl: string;
};

const RequestToken = ({ email, updateEmail, handleEmailSubmit, source, redirectUrl, onClose }: RequestTokenProps) => {
  const [showTerms, setShowTerms] = useState(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const query = new URLSearchParams(location.search);
  const giftcardToken = query.get('giftcardToken');

  const handleLoginClick = () => {
    trackMpEvent(EVENT_NAME.LOGIN_CONTINUE_CLICK, { screen_name: SCREEN_NAME.LOGIN_START });
    handleEmailSubmit({ triggerSource: source, redirectUrl });
  };

  const handleOnBlur = () => {
    if (email.value) return;
    setShowTerms(false);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateEmail(e.target.value);
    if (e.target.value) {
      setShowTerms(true);
    }
  };

  const handleOnCancelBookingClick = () => {
    dispatch(modalActions.login({ show: false }));
    onClose();
  };

  return (
    <>
      <div className="gap-md flex flex-col">
        {Boolean(giftcardToken) && (
          <div className="pb-md">
            <Alert
              leftSlot={<Icon size="sm" variant="info-circle" />}
              variant="information"
              verticalAlign="top"
              rightSlot={<p>{_s(`${translationKey}.savedGiftcard`)}</p>}
            />
          </div>
        )}

        <ListInput
          id="email"
          type="email"
          autoComplete="email"
          leftIcon={<Icon variant="email" />}
          leftPadding={false}
          rightPadding={false}
          label={_s(`${translationKey}.input.title`)}
          placeholder={_s(`${translationKey}.input.placeholder`)}
          inputValue={email.value}
          value={email.value}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          setValue={() => updateEmail('')}
          error={email.error && email.submitting ? _s(`${translationKey}.input.error`) : undefined}
          data-cy="login-email-input"
          floatStyle={true}
          maxLength={254}
        />
        <Button block variant="primary" size="md" onClick={handleLoginClick} data-cy="login-submit-button">
          {_s(`${translationKey}.cta`)}
        </Button>
        {showTerms && (
          <p className="text-fg_secondary text-m text-center">
            {_s(`${translationKey}.terms.text`)}{' '}
            <Link to="/articles/terms-booking-consumer" target="_blank" className="text-nowrap underline">
              {_s(`${translationKey}.terms.link`)}
            </Link>
          </p>
        )}
        {!showTerms && (
          <ListItem.Base to="/cancel" onClick={handleOnCancelBookingClick}>
            <div className="gap-xs flex items-center">
              <Icon variant="calendar-cross" />
              <span>{_s(`${translationKey}.cancelWithCode`)}</span>
            </div>
          </ListItem.Base>
        )}
      </div>
    </>
  );
};

export default RequestToken;
